// Adapted from here: https://github.com/minwork/use-long-press/blob/master/src/index.ts

import React from 'react'
import { merge } from 'lodash'

const getCurrentPosition = event => {
  return {
    x: event.touches[0].pageX,
    y: event.touches[0].pageY
  }
}

const noop = () => {}

const useLongTouch = (callback, options) => {
  const isLongPressActive = React.useRef(false)
  const isPressed = React.useRef(false)
  const timer = React.useRef()
  const savedCallback = React.useRef(callback)
  const startPosition = React.useRef(null)
  const endPosition = React.useRef(null)
  const { threshold, onStart, onMove, onFinish, onCancel } = React.useMemo(
    () =>
      merge(options ?? {}, {
        threshold: 400,
        onStart: noop,
        onMove: noop,
        onFinish: noop,
        onCancel: noop
      }),
    [options]
  )

  const onTouchStart = React.useCallback(
    event => {
      event.persist()

      if (isPressed.current) {
        return
      }

      const currentPosition = getCurrentPosition(event)
      startPosition.current = currentPosition
      endPosition.current = currentPosition

      onStart(event)

      isPressed.current = true

      timer.current = setTimeout(() => {
        if (savedCallback.current) {
          savedCallback.current(event)
          isLongPressActive.current = true
        }
      }, threshold)
    },
    [onStart, threshold]
  )

  const onTouchEnd = React.useCallback(
    event => {
      startPosition.current = null

      event.persist()

      if (isLongPressActive.current) {
        onFinish(event)
      } else if (isPressed.current) {
        onCancel(event)
      }

      isLongPressActive.current = false
      isPressed.current = false
      timer.current !== undefined && clearTimeout(timer.current)
    },
    [onFinish, onCancel]
  )

  const onTouchMove = React.useCallback(
    event => {
      event.persist()

      onMove(event)

      if (!startPosition.current) {
        return
      }

      const currentPosition = getCurrentPosition(event)
      endPosition.current = currentPosition

      const moveThreshold = 25
      const movedDistance = {
        x: Math.abs(currentPosition.x - startPosition.current.x),
        y: Math.abs(currentPosition.y - startPosition.current.y)
      }

      if (movedDistance.x > moveThreshold || movedDistance.y > moveThreshold) {
        onTouchEnd(event)
      }
    },
    [onMove, onTouchEnd]
  )

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    position: endPosition.current
  }
}

export default useLongTouch
